import React, { useRef, useState } from 'react';

import { catalogApi } from 'api/catalog';

function BulkUploadItemInformationPhotos() {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [validationResults, setValidationResults] = useState([]);
    const [tableRows, setTableRows] = useState([]);
    const fileInputRef = useRef(null);

    const allowedExtensions = ['jpg', 'jpeg', 'png'];
    // Patterns for parsing and strict validation
    const baseNamePattern = /^(\d+)_photo(\d+)/i;
    const strictPattern = /^(\d+)_photo(\d+)\.(jpg|jpeg|png)$/i;
    const nutrStrictPattern = /^(\d+)_nutr\.(jpg|jpeg|png)$/i;

    const validateFile = (file) => {
        const errors = [];
        const fileName = file.name || '';
        const extension = (fileName.split('.').pop() || '').toLowerCase();

        let id = null;
        let photoNumber = null;
        let kind = null;

        const baseMatch = fileName.match(baseNamePattern);
        if (baseMatch) {
            id = baseMatch[1];
            photoNumber = Number(baseMatch[2]);
            kind = 'photo';
        }
        const nutrMatch = fileName.match(nutrStrictPattern);
        if (nutrMatch) {
            id = nutrMatch[1];
            kind = 'nutr';
        }

        if (!allowedExtensions.includes(extension)) {
            errors.push(`Invalid file type: .${extension}. Allowed: .jpg, .jpeg, .png`);
        }

        const strictMatch = fileName.match(strictPattern);
        const strictNutrMatch = fileName.match(nutrStrictPattern);
        if (!strictMatch && !strictNutrMatch) {
            errors.push('Invalid file name format. Expected {id}_photo# or {id}_nutr');
        }

        if (kind === 'photo' && photoNumber != null && (photoNumber < 1 || photoNumber > 5)) {
            errors.push(`photo ${photoNumber} is invalid`);
        }

        return {
            file,
            fileName,
            id,
            photoNumber,
            kind,
            isValid: errors.length === 0,
            errors,
        };
    };

    const buildTableRows = (results) => {
        const map = new Map();
        for (const r of results) {
            const rowId = r.id || '(unknown)';
            if (!map.has(rowId)) {
                map.set(rowId, {
                    id: rowId,
                    errors: [],
                    warnings: [],
                    photos: { 1: null, 2: null, 3: null, 4: null, 5: null },
                    existing: { 1: false, 2: false, 3: false, 4: false, 5: false },
                    nutr: null,
                    existingNutr: false,
                });
            }
            const row = map.get(rowId);
            if (r.errors.length > 0) {
                row.errors.push(...r.errors);
            } else if (r.kind === 'photo' && r.photoNumber != null) {
                if (row.photos[r.photoNumber]) {
                    row.errors.push(`duplicate photo ${r.photoNumber}`);
                } else {
                    row.photos[r.photoNumber] = r;
                }
            } else if (r.kind === 'nutr') {
                if (row.nutr) {
                    row.errors.push('duplicate nutritionalLabel');
                } else {
                    row.nutr = r;
                }
            }
        }
        return Array.from(map.values());
    };

    const normalizePhotoInfo = (payload) => {
        const resultMap = new Map();
        if (!payload) return resultMap;
        const photoInfo = payload?.data?.photoInfo ?? payload;
        const handleEntry = (id, data) => {
            const photos = { 1: false, 2: false, 3: false, 4: false, 5: false };
            for (let n = 1; n <= 5; n++) {
                photos[n] = Boolean(data?.[`photo${n}`] ?? data?.[n]);
            }
            const exists = Boolean(data?.exists ?? true);
            resultMap.set(String(id), { exists, photos });
        };
        if (photoInfo && typeof photoInfo === 'object' && !Array.isArray(photoInfo)) {
            for (const [id, data] of Object.entries(photoInfo)) {
                handleEntry(id, data);
            }
        } else if (Array.isArray(photoInfo)) {
            for (const entry of photoInfo) {
                if (Array.isArray(entry)) {
                    handleEntry(entry[0], entry[1] || {});
                } else if (entry && typeof entry === 'object') {
                    const id = entry.id ?? entry.ID ?? entry.itemID ?? entry.externalSku ?? entry.extSku;
                    const data = entry.photos ?? entry;
                    if (id != null) handleEntry(id, data);
                }
            }
        }
        return resultMap;
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files || []);
        setSelectedFiles(files);
        setUploadReport(null);
        setIsUploading(false);
        const results = files.map((file) => validateFile(file));
        setValidationResults(results);
        const baseRows = buildTableRows(results);
        setTableRows(baseRows);

        // Fetch existing photo info for these IDs and merge into rows
        (async () => {
            const ids = Array.from(new Set(results.map((r) => r.id).filter((x) => x && x !== '(unknown)')));
            if (ids.length === 0) return;
            try {
                const apiResp = await catalogApi.getItemInformationPhotoInfo(ids);
                const existingMap = normalizePhotoInfo(apiResp);
                setTableRows((prev) =>
                    prev.map((row) => {
                        const fromApi = existingMap.get(String(row.id));
                        if (!fromApi) return row;
                        const merged = {
                            ...row,
                            existing: { ...row.existing, ...fromApi.photos },
                            existingNutr: fromApi.nutr || row.existingNutr,
                        };
                        const newErrors = [...merged.errors];
                        if (fromApi.exists === false) {
                            newErrors.push('ID does not exist');
                        }
                        const newWarnings = [...(merged.warnings || [])];
                        for (let n = 1; n <= 5; n++) {
                            if (merged.photos[n] && fromApi.photos[n]) {
                                newWarnings.push(`photo ${n} already exists and will be overwritten`);
                            }
                        }
                        if (merged.nutr && fromApi.nutr) {
                            newWarnings.push('nutritional label already exists and will be overwritten');
                        }
                        merged.errors = newErrors;
                        merged.warnings = newWarnings;
                        return merged;
                    }),
                );
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to fetch existing photo info', e);
            }
        })();
    };

    const [isUploading, setIsUploading] = useState(false);
    const [uploadReport, setUploadReport] = useState(null);
    const allValid = tableRows.length > 0 && tableRows.every((r) => r.errors.length === 0);

    const handleUpload = async () => {
        if (!allValid || isUploading) {
            return;
        }
        setIsUploading(true);
        setUploadReport(null);
        const rows = tableRows;
        const results = [];
        const perPhoto = [];
        for (const row of rows) {
            try {
                const formData = new FormData();
                let hasFile = false;
                const includedPhotos = [];
                for (let n = 1; n <= 5; n++) {
                    const entry = row.photos[n];
                    if (entry && entry.file) {
                        hasFile = true;
                        const key = n === 1 ? 'productPhoto' : `photo${n}Photo`;
                        formData.append(key, entry.file);
                        includedPhotos.push(n);
                    }
                }
                if (row.nutr && row.nutr.file) {
                    hasFile = true;
                    formData.append('nutritionalPhoto', row.nutr.file);
                    includedPhotos.push('nutr');
                }
                if (!hasFile) {
                    results.push({ id: row.id, status: 'skipped', msg: 'No new photos selected' });
                    continue;
                }
                formData.append('externalSku', row.id);
                const res = await catalogApi.updateItemInformation(formData);
                if (res && res.status === true) {
                    results.push({ id: row.id, status: 'ok' });
                    for (const n of includedPhotos) {
                        perPhoto.push({ id: row.id, photo: n, status: 'ok', msg: '' });
                    }
                } else {
                    const errorMsg = res?.msg || 'Unknown error';
                    results.push({ id: row.id, status: 'error', msg: errorMsg });
                    for (const n of includedPhotos) {
                        perPhoto.push({ id: row.id, photo: n, status: 'error', msg: errorMsg });
                    }
                }
            } catch (e) {
                const errorMsg = e?.message || String(e);
                results.push({ id: row.id, status: 'error', msg: errorMsg });
                // Attribute error to all included photos for this ID
                for (let n = 1; n <= 5; n++) {
                    const entry = row.photos[n];
                    if (entry && entry.file) {
                        perPhoto.push({ id: row.id, photo: n, status: 'error', msg: errorMsg });
                    }
                }
            }
        }
        const success = results.filter((r) => r.status === 'ok').map((r) => r.id);
        const failed = results.filter((r) => r.status === 'error');
        const skipped = results.filter((r) => r.status === 'skipped').map((r) => r.id);
        setUploadReport({ success, failed, skipped, perPhoto });
        setIsUploading(false);
    };

    const statusStyle = (isValid) => ({
        color: isValid ? 'green' : 'red',
        fontWeight: '600',
    });

    const handleClear = () => {
        setSelectedFiles([]);
        setValidationResults([]);
        setTableRows([]);
        setUploadReport(null);
        setIsUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div>
            <h1>Bulk Upload Item Information Photos</h1>

            {selectedFiles.length === 0 && (
                <div style={{ margin: '12px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        multiple
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        style={{
                            padding: '8px 12px',
                            background: '#1976d2',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                        }}
                    >
                        Upload Photos
                    </button>
                    <span style={{ color: '#555' }}>Accepts JPG, JPEG, PNG. Use filename format {`{id}_photo#`}.</span>
                </div>
            )}

            {selectedFiles.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '12px' }}>
                        <button
                            type="button"
                            onClick={handleClear}
                            style={{
                                padding: '8px 12px',
                                background: '#888',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                            }}
                        >
                            Clear
                        </button>
                        {allValid ? (
                            <button
                                type="button"
                                onClick={handleUpload}
                                style={{
                                    padding: '8px 12px',
                                    background: '#0b7',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                }}
                                disabled={isUploading}
                            >
                                Confirm
                            </button>
                        ) : null}
                        {!allValid && (
                            <span style={{ color: 'red' }}>Please fix the errors below before confirming.</span>
                        )}
                        {allValid &&
                            !isUploading &&
                            !(uploadReport && uploadReport.perPhoto && uploadReport.perPhoto.length > 0) && (
                                <span style={{ color: 'green' }}>All files valid. Ready to confirm.</span>
                            )}
                        {isUploading && (
                            <span style={{ color: '#555' }}>
                                Uploading images... Please do not navigate away from this page.
                            </span>
                        )}
                    </div>

                    {isUploading ? (
                        <div style={{ padding: '24px', textAlign: 'center', color: '#555' }}>Loading...</div>
                    ) : uploadReport && uploadReport.perPhoto && uploadReport.perPhoto.length > 0 ? (
                        <div style={{ overflowX: 'auto' }}>
                            <div style={{ marginBottom: '8px', color: 'green' }}>Uploads complete.</div>
                            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th
                                            style={{
                                                textAlign: 'left',
                                                borderBottom: '1px solid #ddd',
                                                padding: '8px',
                                            }}
                                        >
                                            id
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'left',
                                                borderBottom: '1px solid #ddd',
                                                padding: '8px',
                                            }}
                                        >
                                            photo
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'left',
                                                borderBottom: '1px solid #ddd',
                                                padding: '8px',
                                            }}
                                        >
                                            status
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'left',
                                                borderBottom: '1px solid #ddd',
                                                padding: '8px',
                                            }}
                                        >
                                            message
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {uploadReport.perPhoto.map((r, idx) => (
                                        <tr key={`upload-${r.id}-${r.photo}-${idx}`}>
                                            <td style={{ borderBottom: '1px solid #f0f0f0', padding: '8px' }}>
                                                {r.id}
                                            </td>
                                            <td
                                                style={{ borderBottom: '1px solid #f0f0f0', padding: '8px' }}
                                            >{`photo${r.photo}`}</td>
                                            <td
                                                style={{
                                                    borderBottom: '1px solid #f0f0f0',
                                                    padding: '8px',
                                                    color: r.status === 'ok' ? 'green' : 'red',
                                                }}
                                            >
                                                {r.status === 'ok' ? 'Success' : 'Error'}
                                            </td>
                                            <td
                                                style={{
                                                    borderBottom: '1px solid #f0f0f0',
                                                    padding: '8px',
                                                    color: r.status === 'ok' ? 'green' : 'red',
                                                }}
                                            >
                                                {r.status === 'ok' ? '—' : r.msg}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        tableRows.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th
                                                style={{
                                                    textAlign: 'left',
                                                    borderBottom: '1px solid #ddd',
                                                    padding: '8px',
                                                }}
                                            >
                                                id
                                            </th>
                                            <th
                                                style={{
                                                    textAlign: 'left',
                                                    borderBottom: '1px solid #ddd',
                                                    padding: '8px',
                                                }}
                                            >
                                                errors
                                            </th>
                                            <th
                                                style={{
                                                    textAlign: 'left',
                                                    borderBottom: '1px solid #ddd',
                                                    padding: '8px',
                                                }}
                                            >
                                                warnings
                                            </th>
                                            <th
                                                style={{
                                                    textAlign: 'left',
                                                    borderBottom: '1px solid #ddd',
                                                    padding: '8px',
                                                }}
                                            >
                                                photo1
                                            </th>
                                            <th
                                                style={{
                                                    textAlign: 'left',
                                                    borderBottom: '1px solid #ddd',
                                                    padding: '8px',
                                                }}
                                            >
                                                photo2
                                            </th>
                                            <th
                                                style={{
                                                    textAlign: 'left',
                                                    borderBottom: '1px solid #ddd',
                                                    padding: '8px',
                                                }}
                                            >
                                                photo3
                                            </th>
                                            <th
                                                style={{
                                                    textAlign: 'left',
                                                    borderBottom: '1px solid #ddd',
                                                    padding: '8px',
                                                }}
                                            >
                                                photo4
                                            </th>
                                            <th
                                                style={{
                                                    textAlign: 'left',
                                                    borderBottom: '1px solid #ddd',
                                                    padding: '8px',
                                                }}
                                            >
                                                photo5
                                            </th>
                                            <th
                                                style={{
                                                    textAlign: 'left',
                                                    borderBottom: '1px solid #ddd',
                                                    padding: '8px',
                                                }}
                                            >
                                                nutritionalLabel
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableRows.map((row) => (
                                            <tr key={`row-${row.id}`}>
                                                <td style={{ borderBottom: '1px solid #f0f0f0', padding: '8px' }}>
                                                    {row.id}
                                                </td>
                                                <td style={{ borderBottom: '1px solid #f0f0f0', padding: '8px' }}>
                                                    {row.errors.length === 0 ? (
                                                        <span style={{ color: 'green' }}>—</span>
                                                    ) : (
                                                        <ul style={{ margin: 0, paddingLeft: '18px', color: 'red' }}>
                                                            {row.errors.map((err, idx) => (
                                                                <li key={`err-${row.id}-${idx}`}>{err}</li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </td>
                                                <td style={{ borderBottom: '1px solid #f0f0f0', padding: '8px' }}>
                                                    {!row.warnings || row.warnings.length === 0 ? (
                                                        <span>—</span>
                                                    ) : (
                                                        <ul
                                                            style={{ margin: 0, paddingLeft: '18px', color: '#e67e22' }}
                                                        >
                                                            {row.warnings.map((w, idx) => (
                                                                <li key={`warn-${row.id}-${idx}`}>{w}</li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </td>
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <td
                                                        key={`photo-${row.id}-${n}`}
                                                        style={{ borderBottom: '1px solid #f0f0f0', padding: '8px' }}
                                                    >
                                                        {row.photos[n] ? (
                                                            <span style={{ color: 'green' }}>
                                                                {row.photos[n].fileName}
                                                            </span>
                                                        ) : row.existing?.[n] ? (
                                                            <span style={{ color: '#1976d2' }}>exists</span>
                                                        ) : (
                                                            <span>—</span>
                                                        )}
                                                    </td>
                                                ))}
                                                <td style={{ borderBottom: '1px solid #f0f0f0', padding: '8px' }}>
                                                    {row.nutr ? (
                                                        <span style={{ color: 'green' }}>{row.nutr.fileName}</span>
                                                    ) : row.existingNutr ? (
                                                        <span style={{ color: '#1976d2' }}>exists</span>
                                                    ) : (
                                                        <span>—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}

                    {uploadReport && (
                        <div style={{ marginTop: '12px' }}>
                            <div style={{ color: 'green' }}>Uploaded: {uploadReport.success.length}</div>
                            <div style={{ color: 'red' }}>Failed: {uploadReport.failed.length}</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export { BulkUploadItemInformationPhotos };
